import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import type { FullATSVacancyResponse, Vacancy } from 'clients/AutoCompleteClient/types';
import type { JobImprovedGetDetailOutput } from 'clients/ReachoutClient/ReachoutClient.types';

import { autoCompleteClient } from 'clients/AutoCompleteClient/AutoCompleteClient';
import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { CandidateOutreachSendMethod } from 'clients/CampaignsClient/CampaignsClient.types';
import { reachoutClient } from 'clients/ReachoutClient/ReachoutClient';
import { logger } from 'config/logger';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { SnackbarContext } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import { InsufficientInformationException } from 'shared/legacy/exceptions/InsufficientInformationException';
import { useRobinJrStore } from 'stores/robin-jr/robin-jr.store';
import { QueryKey } from 'types/query';
import { htmlToPlainTextWithHyphenatedLists } from 'utils/shared/htmlToPlainTextWithHyphenatedLists';
import { getVacancyName } from 'utils/vacancyEnrichedUtils';
import { getJobImprovedCacheKey } from './useJobImprovedQuery';

type GenerateReachoutMessageParams = {
  vacancy: Vacancy;
  candidate: { es_person_id: string; resume: string };
  outreach_type: Extract<CandidateOutreachSendMethod, 'email' | 'linkedin_inmail' | 'indeed' | 'whatsapp'>;
  language: string;
  origin?: string;
};

const filterJobForMessageGeneration = (job: JobImprovedGetDetailOutput) => {
  const unwantedFields = [
    'id',
    'created_at',
    'updated_at',
    'status',
    'is_deleted',
    'user_id',
    'tenant_id',
    'job_ats_id',
    'job_campaign_id',
    'type',
    'origin_text',
    'origin_job_title',
    'quality_percent',
    'detected_language',
    'missing_fields',
  ];

  const filteredJob = Object.fromEntries(Object.entries(job).filter(([key]) => !unwantedFields.includes(key)));

  return JSON.stringify(filteredJob);
};

export const useGenerateReachoutMessageMutation = () => {
  const setIsGeneratingMessage = useRobinJrStore((s) => s.setIsGeneratingMessage);
  const { createSnackbar } = useContext(SnackbarContext);
  const { dictionary } = useContext(LocalizationContext);
  const queryClient = useQueryClient();

  return useMutation(
    async ({ candidate, vacancy, outreach_type, language, origin }: GenerateReachoutMessageParams) => {
      let jobTitle = getVacancyName(vacancy);
      let jobDescription = '';
      let atsObject: Partial<FullATSVacancyResponse> = {};

      if (vacancy.type === 'INDEED' && vacancy.originalIndeedJob?.description) {
        jobDescription = JSON.stringify({
          origin_text: vacancy.originalIndeedJob.description,
          employment_requirements: {
            address: vacancy.originalIndeedJob?.location ?? 'Location not informed',
          },
        });
      }

      if (!vacancy.is_improved && vacancy.type === 'ATS_RAW') {
        const atsId = vacancy.job_ats_id;

        await queryClient.prefetchQuery([QueryKey.useAtsVacancyFullQuery, atsId], () =>
          autoCompleteClient.fullAtsVacancy(atsId).then((res) => res.data),
        );

        const fullAts = queryClient.getQueryData<FullATSVacancyResponse>([QueryKey.useAtsVacancyFullQuery, atsId]);

        if (!fullAts?.description || (fullAts.description.length ?? 0) < 150 || !candidate.resume) {
          throw new InsufficientInformationException();
        }

        jobTitle = fullAts.title;
        jobDescription = fullAts.description;
        atsObject = fullAts;
      }

      if (vacancy.is_improved && vacancy.db_id) {
        const dbId = Number(vacancy.db_id);

        await queryClient.prefetchQuery({
          queryKey: getJobImprovedCacheKey(dbId),
          queryFn: () => reachoutClient.getJobImproved(dbId).then((res) => res.data),
        });

        const improvedJob = queryClient.getQueryData<JobImprovedGetDetailOutput>(getJobImprovedCacheKey(dbId));
        if (!improvedJob) {
          throw new Error('Failed to fetch improved job');
        }

        jobTitle = improvedJob.job_details?.job_title;
        jobDescription = filterJobForMessageGeneration(improvedJob);
      }

      const { data: response } = await campaignsClient.generateReachoutMessage({
        vacancy: { ...atsObject, name: jobTitle, description: jobDescription },
        candidate: candidate,
        outreach_type:
          outreach_type === CandidateOutreachSendMethod.Whatsapp
            ? CandidateOutreachSendMethod.Whatsapp
            : CandidateOutreachSendMethod.Email,
        language: language,
        ...(origin != null && origin !== '' ? { origin } : {}),
      });

      if (outreach_type === CandidateOutreachSendMethod.Indeed) {
        response.message = htmlToPlainTextWithHyphenatedLists(response.message);
      }

      return response;
    },
    {
      onError: (error) => {
        logger.error(error);

        if (error instanceof InsufficientInformationException) {
          return;
        }

        createSnackbar(error instanceof Error ? error.message : dictionary.somethingWentWrong, {
          variant: 'danger',
          duration: 10_000,
        });
      },
      onMutate: () => setIsGeneratingMessage(true),
      onSettled: () => setIsGeneratingMessage(false),
    },
  );
};
